import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import ArticleLayout, { H2, H3, P, Li, Ul, Blockquote, DataBox } from './ArticleLayout';
import { ARTICLES } from './RecursosPage';

const meta = ARTICLES[0]; // sistema-multiagente-2027

export default function ArticuloMultiagente() {
  return (
    <>
      <SEO
        title="Qué es un sistema multiagente y por qué tu empresa lo necesitará en 2027 | Neuralia 369"
        description="Gartner estima que el 70 % de los sistemas multiagente tendrán agentes con roles especializados para 2027. Te explicamos qué los diferencia de la automatización por reglas."
        path="/recursos/sistema-multiagente-2027"
      />
      <ArticleLayout
        meta={meta}
        cta={{
          title: '¿Listo para operar con un equipo de agentes?',
          desc: 'Aynara coordina tu pipeline de agentes con control humano en cada punto de decisión. Sin código, sin consultoría larga.',
          href: '/activar',
          label: 'Solicitar activación',
          secondary: { href: '/sistema-multiagente', label: 'Ver el sistema en detalle' },
        }}
      >
        {/* ── INTRO ── */}
        <P>
          En 2023, la mayoría de las empresas que adoptaron inteligencia artificial lo hicieron integrando un chatbot o
          automatizando una tarea concreta con un flujo de reglas. Funcionó. Pero el salto que se acerca es de otra magnitud.
          En 2027, según Gartner, no hablaremos de agentes individuales sino de ecosistemas donde varios agentes
          especializados trabajan en red, cada uno con un rol definido, herramientas propias y capacidad de razonamiento
          dentro de límites establecidos por la empresa.
        </P>

        <DataBox
          stat="70 %"
          label="de los sistemas multiagente tendrán agentes con roles especializados para 2027"
          source="Gartner, Hype Cycle for Artificial Intelligence, 2025"
          color="cyan"
        />

        <P>
          Este artículo explica qué distingue a un sistema multiagente de la automatización tradicional, por qué ese
          salto importa para tu operación y qué debes considerar antes de adoptar esta arquitectura.
        </P>

        {/* ── SECCIÓN 1 ── */}
        <H2>La automatización por reglas y sus límites reales</H2>
        <P>
          La automatización por reglas —RPA, flujos de trabajo, scripts— funciona bien en entornos predecibles.
          Si X entonces Y. Si el cliente escribe "cancelar pedido", redirigir al formulario de devolución.
          Si la factura supera 5 000 €, escalar a aprobación manual. Esas reglas resuelven una enorme cantidad
          de trabajo repetitivo y de bajo riesgo.
        </P>
        <P>
          El problema es que el mundo real rara vez es predecible al 100 %. Los clientes no se comportan
          como el flujo espera. Los documentos llegan con formatos distintos. Las excepciones se multiplican.
          Y cada excepción que no está cubierta en el flujo se convierte en fricción: alguien tiene que intervenir,
          decidir y, si no hay nadie disponible, el proceso se detiene.
        </P>
        <P>
          La automatización por reglas no razona. Ejecuta. Cuando la situación no encaja exactamente con la regla,
          falla o deriva al humano por defecto. Eso limita cuánto puede automatizarse realmente sin generar más
          trabajo de supervisión del que ahorra.
        </P>

        {/* ── SECCIÓN 2 ── */}
        <H2>Agentes que razonan dentro de guardrails: qué cambia</H2>
        <P>
          Un agente de IA no ejecuta una regla fija. Recibe un objetivo ("clasifica este lead y decide el siguiente
          paso en el pipeline"), interpreta el contexto disponible, selecciona qué herramienta usar y genera una
          acción o una recomendación. Lo hace dentro de los límites que la empresa ha definido explícitamente:
          los guardrails.
        </P>
        <P>
          Los guardrails son las restricciones operativas del agente: qué herramientas puede usar, qué umbrales
          no puede cruzar sin aprobación humana, qué datos puede leer y cuáles están fuera de su alcance.
          La diferencia clave respecto a la automatización clásica es que el agente adapta su comportamiento al
          contexto sin necesitar una regla explícita para cada caso posible. Pero nunca sale del perímetro
          que la empresa ha trazado.
        </P>

        <Blockquote source="Princípio de diseño — Neuralia 369">
          Un agente sin guardrails es un riesgo operativo. Un agente con guardrails demasiado rígidos es una
          automatización de reglas cara. El equilibrio está en definir qué puede decidir el agente solo y qué
          requiere la firma humana.
        </Blockquote>

        <H3>Autonomía donde puede, supervisión donde debe</H3>
        <P>
          Esta es la arquitectura que Neuralia 369 llama "human-in-the-loop por diseño": el agente actúa de forma
          autónoma en las decisiones de bajo riesgo y bajo coste de error, y escala automáticamente las decisiones
          de alto impacto para aprobación humana explícita. No como excepción, sino como parte del flujo diseñado.
        </P>
        <Ul>
          <Li>El agente de ventas clasifica leads, actualiza etapas y programa citas sin intervención humana.</Li>
          <Li>Cuando un lead supera cierto score o implica un cobro, el agente detiene la acción y solicita aprobación.</Li>
          <Li>El agente de cobros genera la factura automáticamente, pero el envío requiere confirmación del responsable.</Li>
          <Li>En todos los casos, cada acción queda auditada con timestamp, contexto y resultado.</Li>
        </Ul>

        {/* ── SECCIÓN 3 ── */}
        <H2>Por qué los sistemas multiagente superan al agente único</H2>
        <P>
          Un agente único intenta hacer todo: entender el contexto, elegir la herramienta, ejecutar la acción
          y gestionar la respuesta. Eso funciona para tareas simples. Para operaciones complejas, el agente
          único acumula responsabilidades contradictorias y empieza a cometer errores de coordinación.
        </P>
        <P>
          Un sistema multiagente distribuye el trabajo entre agentes especializados, igual que lo hace
          un equipo humano. El agente de ventas sabe de pipeline y scoring. El agente de agenda sabe de
          disponibilidad y recordatorios. El agente de cobros sabe de facturas y plazos. Aynara, el orquestador,
          decide qué agente actúa en cada momento, con qué datos y dentro de qué límites.
        </P>

        <DataBox
          stat="4 agentes"
          label="especializados coordinados por Aynara en el ecosistema de Neuralia 369 (Ventas, Agenda, CRM, Cobros)"
          source="Arquitectura interna Neuralia 369 — 2025"
          color="cyan"
        />

        <H3>Ventajas operativas del modelo multiagente</H3>
        <Ul>
          <Li>Cada agente puede usar el modelo de lenguaje más eficiente para su tarea específica, reduciendo costes de inferencia.</Li>
          <Li>Si un agente falla, el sistema puede aislar el fallo sin detener el pipeline completo.</Li>
          <Li>Los permisos son granulares por agente: el de ventas no puede acceder a datos financieros del agente de cobros.</Li>
          <Li>Escalar el sistema implica añadir un nuevo agente especializado, no reescribir el flujo completo.</Li>
          <Li>La supervisión humana es más precisa porque cada decisión tiene un agente responsable claro.</Li>
        </Ul>

        {/* ── SECCIÓN 4 ── */}
        <H2>Qué considerar antes de adoptar esta arquitectura</H2>
        <P>
          La adopción de un sistema multiagente no es un proyecto de tecnología, es un proyecto de diseño
          operativo. Antes de empezar, hay tres preguntas que deben responderse con claridad:
        </P>
        <H3>1. ¿Qué decisiones puede tomar el agente sin supervisión?</H3>
        <P>
          La respuesta debe ser explícita y documentada. No basta con "las de bajo riesgo". Hay que definir
          umbrales numéricos, tipos de acción y condiciones de escalado antes de desplegar el agente en producción.
        </P>
        <H3>2. ¿Cómo se audita cada acción del agente?</H3>
        <P>
          Todo sistema multiagente en producción debe mantener un log completo de decisiones: qué agente actuó,
          qué herramienta usó, qué dato leyó, qué acción generó y cuál fue el resultado. Sin esa trazabilidad,
          es imposible identificar errores o mejorar el sistema con el tiempo.
        </P>
        <H3>3. ¿Quién tiene autoridad para revocar un agente?</H3>
        <P>
          La revocación debe ser instantánea y no requerir intervención técnica. Si algo sale mal, el responsable
          operativo debe poder detener un agente desde el panel en segundos, sin necesidad de contactar con soporte.
        </P>

        {/* ── CONCLUSIÓN ── */}
        <H2>2027 no está lejos</H2>
        <P>
          El 70 % que menciona Gartner no es una proyección especulativa. Las empresas que ya están
          experimentando con agentes especializados en 2025 y 2026 serán las que lideren la adopción
          en 2027. El punto de ventaja no está en ser el primero en desplegar un agente, sino en
          entender la arquitectura antes de que todos lo hagan.
        </P>
        <P>
          Si quieres ver cómo funciona en la práctica, el{' '}
          <Link to="/sistema-multiagente" className="text-cyan-300 hover:text-white underline underline-offset-4 decoration-cyan-400/40 transition-colors">
            sistema multiagente de Neuralia 369
          </Link>{' '}
          muestra el diagrama completo del ecosistema: qué agentes existen, cómo se coordinan y dónde
          está el control humano en cada punto del pipeline.
        </P>
      </ArticleLayout>
    </>
  );
}
